import { environment } from '#environments';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, PublicClientApplication } from '@azure/msal-browser';

function msalInstanceFactory() {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: `${location.protocol}//${location.hostname}${location.port && ':' + location.port}/`,
      postLogoutRedirectUri: '/',
      navigateToLoginRequestUrl: false,
      knownAuthorities: environment.knownAuthorities,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false,
    },
  });
}

export function buildProtectedResourceMap(
  engineApiUrl = environment.engineApiUrl,
  engineScope = environment.engineScope,
): Map<string, string[]> {
  const protectedResourceMap = new Map<string, string[]>([
    [environment.apiUrl, [environment.audience + '/user_impersonation']],
    ['https://graph.microsoft.com/v1.0/me', ['User.Read']],
  ]);

  if (engineApiUrl && engineScope) {
    protectedResourceMap.set(engineApiUrl, [engineScope]);
  }

  return protectedResourceMap;
}

function msalInterceptorConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    // strictMatching defaults to true in MSAL Angular v5; set to false to preserve v4 prefix-based URL matching
    strictMatching: false,
    protectedResourceMap: buildProtectedResourceMap(),
  };
}

function msalGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.audience + '/user_impersonation', 'User.Read'],
    },
    loginFailedRoute: './403',
  };
}

export function provideMsalAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ]);
}
